import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { workflowScheduleKeys, workflowUnattendedApprovalKeys } from '~/lib/query-keys';

export interface WorkflowAutoApprovalChange {
  workflowId: string;
  approved: boolean;
}

/**
 * The one place this route's URL is built. The workflow editor's own switch
 * (FRONT-1907) writes the same record from a different surface, and two
 * hand-written paths are how one of them ends up unencoded.
 */
export function workflowAutoApprovalPath(workflowId: string): string {
  return `/api/workflow/${encodeURIComponent(workflowId)}/auto-approval`;
}

/** The surface a write came from. Each one names itself, see the fanout below. */
export type WorkflowAutoApprovalWriter = 'schedule-row' | 'editor-switch';

/**
 * One record, two surfaces that write it: the Schedule row and the editor's own
 * switch (FRONT-1907). Each holds it under a different key — the schedule list
 * as part of its row, the editor library under a key of its own — so a write
 * from either has to reach both, and that fanout lives here rather than at the
 * two call sites.
 *
 * Not optional politeness: the shared client keeps data fresh for a minute and
 * never refetches a query that is already mounted, so a surface missed here
 * goes on reading its stale answer for as long as it stays open. The Schedule
 * row links straight into the editor, which makes "flip it, then click through"
 * a two-second gesture — and the stale answer is about whether a scheduled run
 * may write without asking.
 *
 * The writer names itself because the two are not symmetric. The Schedule row
 * has no optimistic update and refetches its own list. The editor's switch is
 * owned by the library, which writes its own cache entry from its own success
 * handler — so invalidating that entry here would buy nothing and cost three
 * things: a second read of an answer already in hand, the control staying
 * disabled for that round trip, and a failed read painting "could not load
 * this" over a write that worked.
 */
export function refreshWorkflowAutoApproval(
  qc: QueryClient,
  workflowId: string,
  writtenBy: WorkflowAutoApprovalWriter,
): Promise<void> {
  const refreshes = [qc.invalidateQueries({ queryKey: workflowScheduleKeys.all })];
  if (writtenBy !== 'editor-switch') {
    refreshes.push(
      qc.invalidateQueries({ queryKey: workflowUnattendedApprovalKeys.detail(workflowId) }),
    );
  }
  return Promise.all(refreshes).then(() => undefined);
}

/**
 * FRONT-1811: the owner's switch that lets one workflow run tier-3 tools without
 * an approval card. Approve is a PUT, revoke a DELETE, on the same route; both
 * surfaces holding this record are refreshed so neither shows a stale answer.
 *
 * Split out of the hook so a test can run the real verb choice and the real
 * refresh without a React tree — this suite has no DOM, and the behaviour worth
 * guarding is all in these three functions rather than in `useMutation`.
 */
export function workflowAutoApprovalMutationOptions(qc: QueryClient) {
  return {
    mutationFn: ({ workflowId, approved }: WorkflowAutoApprovalChange) => {
      const path = workflowAutoApprovalPath(workflowId);
      return approved ? api.put<{ approved: boolean }>(path) : api.delete<{ approved: boolean }>(path);
    },
    onSuccess: (_res: { approved: boolean }, { workflowId }: WorkflowAutoApprovalChange) =>
      refreshWorkflowAutoApproval(qc, workflowId, 'schedule-row'),
    onError: (_err: unknown, { approved }: WorkflowAutoApprovalChange) =>
      toast.error(approved ? 'Could not approve this workflow' : 'Could not revoke the approval'),
  };
}

export function useSetWorkflowAutoApproval() {
  return useMutation(workflowAutoApprovalMutationOptions(useQueryClient()));
}
